import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, ExternalLink, FileSearch, X } from 'lucide-react';
import type { ComplianceItem } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { formatDateMedium } from '@/lib/hr';
import { sanitizeFileNamePart } from '@/utils/download';
import { t } from '@/i18n';
import { complianceTypeLabel } from './complianceMeta';

interface ComplianceReviewQueueProps {
  nip: string;
}

/**
 * "Dokumenty do weryfikacji" — pending compliance submissions the caller may
 * decide on (their direct reports; for hr/admin the whole company). Preview
 * opens the submitted file via a pre-signed URL; verify/reject mirror the
 * absence approval flow (reject requires a reason, e-mailed to the employee).
 */
export function ComplianceReviewQueue({ nip }: ComplianceReviewQueueProps) {
  const { listCompliance, verifyCompliance, rejectCompliance, complianceDocumentDownload } = useHrApi();
  const [items, setItems] = useState<ComplianceItem[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [rejectFor, setRejectFor] = useState<ComplianceItem | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    let mounted = true;
    listCompliance(nip, { status: 'pending_verification' })
      .then((rows) => {
        if (mounted) setItems(rows.filter((r) => r.canVerify && !r.isSelf));
      })
      .catch((error) => {
        if (mounted) setItems([]);
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      });
    return () => { mounted = false; };
  }, [nip, refreshKey, listCompliance]);

  // "Preview" now SAVES the submitted file (streamed through our API) —
  // window.open is blocked in some hosts, see utils/download.ts. Extension is
  // inferred from the response type when the stored filename is missing.
  const handlePreview = async (item: ComplianceItem) => {
    try {
      await complianceDocumentDownload(
        nip,
        item.id,
        item.documentFileName ||
          `${sanitizeFileNamePart(complianceTypeLabel(item.type))}-${sanitizeFileNamePart(item.employeeName)}`,
      );
    } catch {
      /* session expired — handled by the global modal */
    }
  };

  const handleVerify = async (item: ComplianceItem) => {
    setBusyId(item.id);
    try {
      await verifyCompliance(nip, item.id);
      toast.success(t('hr.compliance.verified'), { description: item.employeeName });
      setRefreshKey((k) => k + 1);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error(t('hr.compliance.actionFailed'), {
          description: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectFor || !reason.trim()) return;
    setBusyId(rejectFor.id);
    try {
      await rejectCompliance(nip, rejectFor.id, reason.trim());
      toast.success(t('hr.compliance.rejectedToast'), { description: rejectFor.employeeName });
      setRejectFor(null);
      setReason('');
      setRefreshKey((k) => k + 1);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error(t('hr.compliance.actionFailed'), {
          description: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) return <Skeleton className="h-32 w-full" />;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileSearch className="text-muted-foreground size-4" />
          {t('hr.compliance.queueTitle')}
        </CardTitle>
        {items.length > 0 && (
          <span className="text-muted-foreground text-xs tabular-nums">{items.length}</span>
        )}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-muted-foreground py-2 text-center text-sm">
            {t('hr.compliance.queueEmpty')}
          </p>
        ) : (
          <ul className="divide-y">
            {items.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {item.employeeName} · {complianceTypeLabel(item.type)}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {item.validUntil && t('hr.compliance.validUntil', { date: formatDateMedium(item.validUntil) })}
                    {item.submittedAt && (
                      <> · {t('hr.compliance.submittedAt', { date: formatDateMedium(item.submittedAt.slice(0, 10)) })}</>
                    )}
                    {item.documentFileName && <> · {item.documentFileName}</>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-7" onClick={() => handlePreview(item)}>
                    <ExternalLink className="size-3.5" />
                    {t('hr.compliance.preview')}
                  </Button>
                  <Button
                    size="sm"
                    className="h-7"
                    disabled={busyId === item.id}
                    onClick={() => handleVerify(item)}
                  >
                    <Check className="size-3.5" />
                    {t('hr.compliance.verify')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive h-7"
                    disabled={busyId === item.id}
                    onClick={() => { setReason(''); setRejectFor(item); }}
                  >
                    <X className="size-3.5" />
                    {t('hr.compliance.rejectAction')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={!!rejectFor} onOpenChange={(open) => !open && setRejectFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('hr.compliance.rejectTitle')}</DialogTitle>
            <DialogDescription>
              {rejectFor &&
                t('hr.compliance.rejectBody', {
                  type: complianceTypeLabel(rejectFor.type),
                  employee: rejectFor.employeeName,
                })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="compliance-reject-reason">{t('hr.compliance.rejectReasonLabel')}</Label>
            <Textarea
              id="compliance-reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('hr.compliance.rejectReasonPlaceholder')}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!reason.trim() || busyId === rejectFor?.id}
              onClick={handleReject}
            >
              {t('hr.compliance.rejectConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
